import { WeeklyAvailability } from "@tellescope/types-models";
import { formatInTimeZone } from "date-fns-tz";
import { getZonedDayStart } from "./get-events-by-date";

export type MinuteRange = [startMinutes: number, endMinutes: number];

const MS_PER_MINUTE = 60_000;

export interface AvailabilityTimeRange {
  start: Date;
  end: Date;
}

export type BufferedEvent = {
  startTimeInMS: number;
  durationInMinutes: number;
  actualDuration?: number;
  bufferStartMinutes?: number;
  bufferEndMinutes?: number;
};

// eslint-disable-next-line @tellescope/require-date-fns-locale
const isoWeekday = (date: Date, timezone: string) => Number(formatInTimeZone(date, timezone, "i"));
// eslint-disable-next-line @tellescope/require-date-fns-locale
const dayOfMonth = (date: Date, timezone: string) => Number(formatInTimeZone(date, timezone, "d"));

export const getDayOfWeekInTimezone = (date: Date, timezone: string) => isoWeekday(date, timezone) % 7;

export const getNthWeekdayOccurrenceInMonth = (date: Date, timezone: string) =>
  Math.ceil(dayOfMonth(date, timezone) / 7) as 1 | 2 | 3 | 4 | 5;

const isWithinActiveRange = (active: WeeklyAvailability["active"], date: Date) => {
  if (!active) return true;
  const time = date.getTime();
  if (active.from instanceof Date && time < active.from.getTime()) return false;
  if (active.to instanceof Date && time > active.to.getTime()) return false;
  return true;
};

export const isAvailabilityBlockActiveOn = (block: WeeklyAvailability, date: Date, timezone: string) => {
  if (block.dayOfWeekStartingSundayIndexedByZero !== getDayOfWeekInTimezone(date, timezone)) return false;
  if (!isWithinActiveRange(block.active, date)) return false;
  if (
    block.monthlyRestriction &&
    !block.monthlyRestriction.occurrences.includes(getNthWeekdayOccurrenceInMonth(date, timezone))
  ) {
    return false;
  }
  return true;
};

export const getMinutesOfDayInTimezone = (date: Date, timezone: string) =>
  // eslint-disable-next-line @tellescope/require-date-fns-locale
  Number(formatInTimeZone(date, timezone, "H")) * 60 + Number(formatInTimeZone(date, timezone, "m"));

export const mergeMinuteRanges = (ranges: MinuteRange[]): MinuteRange[] => {
  const normalized = ranges.filter(([start, end]) => end > start).sort(([a], [b]) => a - b);
  return normalized.reduce<MinuteRange[]>((acc, [start, end]) => {
    const last = acc[acc.length - 1];
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
      return acc;
    }
    acc.push([start, end]);
    return acc;
  }, []);
};

export const complementRanges = (covered: MinuteRange[], viewStart: number, viewEnd: number): MinuteRange[] => {
  if (viewEnd <= viewStart) return [];
  const clamped = covered
    .map<MinuteRange>(([start, end]) => [Math.max(start, viewStart), Math.min(end, viewEnd)])
    .filter(([start, end]) => end > start);
  const merged = mergeMinuteRanges(clamped);
  const { gaps, cursor } = merged.reduce<{ gaps: MinuteRange[]; cursor: number }>(
    (acc, [start, end]) => {
      if (start > acc.cursor) acc.gaps.push([acc.cursor, start]);
      acc.cursor = Math.max(acc.cursor, end);
      return acc;
    },
    { gaps: [], cursor: viewStart }
  );
  if (cursor < viewEnd) gaps.push([cursor, viewEnd]);
  return gaps;
};

export const isUserAvailableForRange = (
  availabilities: WeeklyAvailability[],
  start: Date,
  durationInMinutes: number,
  timezone: string
) => {
  if (availabilities.length === 0) return true;
  const activeBlocks = availabilities.filter((block) => isAvailabilityBlockActiveOn(block, start, timezone));
  if (activeBlocks.length === 0) return false;
  const merged = mergeMinuteRanges(
    activeBlocks.map<MinuteRange>((block) => [block.startTimeInMinutes, block.endTimeInMinutes])
  );
  const startMinutes = getMinutesOfDayInTimezone(start, timezone);
  return complementRanges(merged, startMinutes, startMinutes + durationInMinutes).length === 0;
};

export const getUnavailableUsers = <T extends { id: string; weeklyAvailabilities?: WeeklyAvailability[] }>(
  users: T[],
  userIds: string[],
  start: Date,
  durationInMinutes: number,
  timezone: string
): T[] => {
  if (userIds.length === 0) return [];
  const selectedIds = new Set(userIds);
  return users.reduce<T[]>((acc, user) => {
    if (
      selectedIds.has(user.id) &&
      !isUserAvailableForRange(user.weeklyAvailabilities ?? [], start, durationInMinutes, timezone)
    ) {
      acc.push(user);
    }
    return acc;
  }, []);
};

/**
 * Splits a user's weekly availability for a single day into the open time ranges that remain after
 * removing the events they are busy with (including each event's and each availability block's
 * buffer). When `nowMs` falls within the day, time before it is treated as busy so no past
 * availability is returned.
 */
export const getAvailableRangesForDay = (
  availabilities: WeeklyAvailability[],
  events: BufferedEvent[],
  day: Date,
  timezone: string,
  nowMs?: number
): AvailabilityTimeRange[] => {
  const dayStart = getZonedDayStart(day, timezone);
  const dayStartMs = dayStart.getTime();

  const activeBlocks = availabilities.filter((block) => isAvailabilityBlockActiveOn(block, dayStart, timezone));
  if (activeBlocks.length === 0) return [];

  const windows = mergeMinuteRanges(
    activeBlocks.map<MinuteRange>((block) => [
      block.startTimeInMinutes + (block.bufferStartMinutes ?? 0),
      block.endTimeInMinutes,
    ])
  );

  const busy = events.map<MinuteRange>((event) => {
    const startMs = event.startTimeInMS - (event.bufferStartMinutes ?? 0) * MS_PER_MINUTE;
    const endMs =
      event.startTimeInMS +
      (event.actualDuration ?? event.durationInMinutes) * MS_PER_MINUTE +
      (event.bufferEndMinutes ?? 0) * MS_PER_MINUTE;
    return [(startMs - dayStartMs) / MS_PER_MINUTE, (endMs - dayStartMs) / MS_PER_MINUTE];
  });

  if (nowMs !== undefined && nowMs > dayStartMs) {
    busy.push([0, (nowMs - dayStartMs) / MS_PER_MINUTE]);
  }

  return windows.reduce<AvailabilityTimeRange[]>((acc, window) => {
    complementRanges(busy, window[0], window[1]).forEach(([start, end]) => {
      acc.push({
        start: new Date(dayStartMs + start * MS_PER_MINUTE),
        end: new Date(dayStartMs + end * MS_PER_MINUTE),
      });
    });
    return acc;
  }, []);
};
